import { type Ref, ref } from "vue";
import { forgetPendingTurn } from "../api/pending-turn";
import { recoverTurn } from "../api/recover-turn";
import type { StreamOutcome } from "../api/stream";
import type { SessionMessage } from "../api/use-agents";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import { type Bubble, rememberedBubble } from "./bubble";

/**
 * O QUE A TELA FAZ QUANDO O FIO MORRE A MEIO DO TURNO.
 *
 * Separado de `use-turn-stream.ts`, que é o TURNO e já está no seu tecto de
 * linhas; aqui está o depois — a busca, a linha discreta e a única confissão
 * honesta. A regra que ela serve está escrita em `recover-turn.ts`: o servidor
 * arquiva a resposta oiça alguém ou não, então uma ligação morta não prova nada
 * sobre o turno.
 *
 * Um `composable` e não um módulo simples exactamente por causa das três frases:
 * são lidas por uma pessoa, vêm do dicionário, e o dicionário precisa de um
 * escopo onde correr.
 */
export function useTurnRecovery(deps: {
	agent: () => string;
	onMessages?: (messages: SessionMessage[]) => void;
	sessionId: () => string | null;
	bubbles: Ref<Bubble[]>;
	liveText: Ref<string>;
	failure: Ref<string>;
	/** O tique de terminado — o mesmo que um turno acabado pelo fio recebe. */
	finished: () => void;
}) {
	const { t } = useAgentsI18n();

	/** A linha baixa sob a thread. Nunca vermelha: ela narra, não alarma. */
	const notice = ref("");
	/** Houve uma queda que a busca não resolveu — a tela oferece olhar outra vez. */
	const lost = ref(false);
	let controller: AbortController | null = null;

	/**
	 * O fio morreu; o turno pode não ter morrido. Reata, relê, e só então confessa.
	 *
	 * `reattach` volta a ligar-se ao turno em voo desenhando pelos MESMOS ouvintes
	 * do envio — é a primeira coisa que se tenta e a que resolve o caso comum, com
	 * o turno ainda a correr no servidor. Vem por argumento e não por dependência
	 * porque os ouvintes pertencem a cada turno, e não a esta busca.
	 */
	async function afterDrop(
		prompt: string,
		reattach: (signal: AbortSignal) => Promise<StreamOutcome>,
	): Promise<void> {
		lost.value = false;
		notice.value = t("recovery.checking");
		const search = new AbortController();
		controller = search;

		const found = await recoverTurn({
			sessionId: deps.sessionId(),
			prompt,
			reattach,
			onMessages: deps.onMessages,
			signal: search.signal,
		});
		if (search.signal.aborted) return;
		controller = null;

		// O fio voltou e correu até ao fim: os ouvintes já desenharam tudo, incluindo
		// o quadro `done`. Não há nada a acrescentar e nada a anunciar.
		if (found?.how === "live") {
			notice.value = "";
			forgetPendingTurn(deps.agent(), deps.sessionId());
			return;
		}

		if (found?.how === "archived") {
			// O arquivado é a resposta INTEIRA; os deltas parciais estão lá dentro.
			deps.liveText.value = "";
			deps.bubbles.value = [...deps.bubbles.value, ...found.replies.map(rememberedBubble)];
			notice.value = t("recovery.arrived");
			forgetPendingTurn(deps.agent(), deps.sessionId());
			deps.finished();
			return;
		}

		notice.value = "";
		// Parada por uma pessoa (envio novo, painel fechado): não há veredito a dar.
		if (search.signal.aborted) return;
		// O que CHEGOU fica no ecrã: a pessoa viu aquilo a ser escrito.
		if (deps.liveText.value.trim()) {
			deps.bubbles.value = [
				...deps.bubbles.value,
				{ role: "assistant", text: deps.liveText.value },
			];
			deps.liveText.value = "";
		}
		/**
		 * A CONFISSÃO, e o que ela deixou de dizer.
		 *
		 * Dizia "mande de novo" — e foi por obedecer a essa frase que o dono pagou
		 * duas vezes um turno de 226 s que o servidor tinha concluído e gravado. Daqui
		 * não se consegue saber se a resposta existe; o que se sabe é que ela PODE
		 * existir, e a frase passa a dizer isso e a oferecer olhar outra vez. O
		 * registo pendente fica de propósito: é por ele que "ver o turno" procura.
		 */
		lost.value = true;
		deps.failure.value = t("recovery.lost");
		deps.bubbles.value = [
			...deps.bubbles.value,
			{ role: "assistant", text: deps.failure.value, failed: true },
		];
	}

	/** Um envio novo ou um painel desmontado tornam a busca inútil. */
	function cancel(): void {
		controller?.abort();
		controller = null;
	}

	return { notice, lost, afterDrop, cancel };
}
